import React, {useEffect, useRef, useState} from "react"
import { graphql, Link, navigate } from "gatsby"
import "../styles/styles.scss"
import { gsap, TweenLite, Power3 } from "gsap";
import SEO from "../components/seo"
import Layout from "../components/layout"
import Instructions from "../components/hotspot/Instructions"
import HotspotContainer from "../components/hotspot/HotspotContainer"
import HotspotInfo from "../components/hotspot/HotspotInfo"
import HotspotText from '../components/hotspot/HotspotText'
import HotspotImage from '../components/hotspot/HotspotImage'
import HotspotMedia from '../components/hotspot/HotspotMedia'
import HotspotCarousel from '../components/hotspot/HotspotCarousel'
import HotspotVimeo from '../components/hotspot/HotspotVimeo'

import { clothEffect } from '../helpers/ThreeJs';
import IdleTimer from 'react-idle-timer'
import ArrowLeft from '../components/fa/ArrowLeft';
gsap.registerPlugin(TweenLite);

export default function Template({
  data, // this prop will be injected by the GraphQL query below.
}) {
  const { markdownRemark } = data // data.markdownRemark holds your post data
  const { frontmatter } = markdownRemark
  const {instructions, textileImage, infoHotspot, textHotspot, imgHotspot, VimeoHotspot, audioHotspot, carouselHotspot} = frontmatter;
 
  const slideUpEl = useRef(null);
  const buttonRef = useRef(null);
  const instructionsRef = useRef(null);
  const mountRef = useRef(null);
  const idleTimerRef = useRef(null);
  const containerRef=useRef(null)
  const [overflow, setOverflow] = useState("overflow-hidden")

  // use effect method runs after initial render so elements exist before we try to change them
  useEffect(() => { 
    const instructionsHeight = `-${instructionsRef.current.clientHeight}`;
    const elements = containerRef.current.children;
    const targets = Object.keys(elements).map((el) => elements[el]).filter(el => el.classList.contains("hotspot"));
    // load cloth image
    if (textileImage) {
      clothEffect(mountRef, textileImage.image);
    }
    TweenLite.set(targets, {opacity: 0});
    TweenLite.set(buttonRef.current, {opacity: 0});      
    TweenLite.set(containerRef.current, { opacity:0 } );
    TweenLite.set(slideUpEl.current, { y: -instructionsHeight} );
    TweenLite.set(instructionsRef.current, { opacity: 0} );
    // fade in instructions text
    TweenLite.to(instructionsRef.current, { 
      opacity: 1,  
      duration: 1.5,
      delay: 1,  
      ease:Power3.easeOut });
    // fade in cloth image
    TweenLite.to(containerRef.current, { 
      opacity: 1,
      duration: 2.5,
      delay: 3,  
      ease:Power3.easeOut 
    });
    // slide up button/canvas container
    TweenLite.to(slideUpEl.current, { 
      y: 0, 
      duration: 1.5, 
      delay: 3,  
      ease:Power3.easeOut})
    // fade in back button
    TweenLite.to(buttonRef.current, { 
      opacity: 1,
      duration: 1.5,  
      ease:Power3.easeOut,
      delay: 5.5 });
    // fade in hotspots one by one
    TweenLite.staggerTo(targets, 1.5, { 
      opacity: 1.5, 
      ease:Power3.easeOut, 
      delay: 5}, .25)
    window.setTimeout(function() {
      instructionsRef.current.classList.add("visually-hidden")
    }, 5200)
  }, [mountRef]);

  const navigateHome = () => {
      navigate("/")
  }

  return (
    <Layout>
      <IdleTimer
        ref={idleTimerRef}
        onIdle={navigateHome}
        timeout={10 * 60 * 1000} />
      <SEO title="Object" />
      <div className={`${overflow} object-container`}>
        <div className="instructions-container" ref={instructionsRef}>
          <Instructions overtitle={instructions.overtitle} text={instructions.text} />
        </div>
        
        <div className="object" ref={slideUpEl}>
          <Link to="/" className="primary-btn back" ref={buttonRef} state={{fromObject:true}}>
            <ArrowLeft />
            Back
          </Link>

          <div className="image-container" ref={containerRef}> 
            <div ref={mountRef}></div>

            {textHotspot !== null && textHotspot.map(hotspot => hotspot.title !== "none" && hotspot.title !== "null" && hotspot.title !== null ? 
              <HotspotContainer 
                hotspot={hotspot}  
                order={hotspot.order} 
                hotspotType={<HotspotText title={hotspot.title} text={hotspot.text}/>}
              />
            : null)}

            {imgHotspot !== null && imgHotspot.map((hotspot) => hotspot.title !== "none" && hotspot.title !== "null" && hotspot.title !== null ? 
                <HotspotContainer 
                  hotspot={hotspot} 
                  order={hotspot.order} 
                  hotspotType={<HotspotImage image={hotspot.image} alt={hotspot.imageAlt} title={hotspot.title} text={hotspot.text}/>} />
              : null)}
          
            {/* {videoHotspot !== null && videoHotspot.map(hotspot => hotspot.title !== "none" && hotspot.title !== "null" && hotspot.title !== null ? 
                <HotspotContainer 
                  hotspot={hotspot}  
                  order={hotspot.order} 
                  hotspotType={<HotspotMedia mediaType="video" file={hotspot.video} title={hotspot.title} text={hotspot.text}/>}/> 
            : null)} */}

            {VimeoHotspot !== null && VimeoHotspot.map(hotspot => hotspot.title !== "none" && hotspot.title !== "null" && hotspot.title !== null ? 
                <HotspotContainer 
                  hotspot={hotspot}  
                  order={hotspot.order} 
                  hotspotType={<HotspotVimeo mediaType="video" file={hotspot.videoId} title={hotspot.title} text={hotspot.text}/>}/> 
            : null)}
            
            {audioHotspot !== null && audioHotspot.map(hotspot => hotspot.title !== "none" && hotspot.title !== "null" && hotspot.title !== null ? 
                <HotspotContainer 
                  hotspot={hotspot} 
                  order={hotspot.order} 
                  hotspotType={<HotspotMedia mediaType="audio" file={hotspot.audio} title={hotspot.title} text={hotspot.text}/>} /> 
            : null)}

            {carouselHotspot !== null && carouselHotspot.map(hotspot => hotspot.title !== "none" && hotspot.title !== "null" && hotspot.title !== null ? 
                <HotspotContainer 
                  hotspot={hotspot} 
                  order={hotspot.order} 
                  hotspotType={<HotspotCarousel images={hotspot.images} title={hotspot.title} text={hotspot.text}/>} /> 
            : null)}

            <HotspotContainer 
              hotspot={infoHotspot}  
              order="info" 
              hotspotType={<HotspotInfo 
                title={infoHotspot.title} 
                subtitle={infoHotspot.subtitle} 
                maker={infoHotspot.maker} origin={infoHotspot.origin} time={infoHotspot.time} size={infoHotspot.size} 
                image={textileImage.image} alt={textileImage.imageAlt} 
                /> 
              } 
            />
          </div>
        </div>
      </div>
    </Layout>
  )
}
export const pageQuery = graphql`
  query($path: String!) {
    markdownRemark(frontmatter: { path: { eq: $path } }) {
      frontmatter {
        path
        instructions {
          overtitle
          text
        }
        textileImage {
          image 
          imageAlt
        }
        infoHotspot {
          title
          subtitle
          maker
          origin
          time
          size
          btnTop
          btnLeft
        }
        textHotspot {
          order
          title
          text
          btnTop
          btnLeft
        }
        imgHotspot {
          order
          image 
          imageAlt
          title
          text
          btnTop
          btnLeft
        }
        VimeoHotspot {
          order
          videoId
          title
          text
          btnTop
          btnLeft
        }
        audioHotspot {
          order
          audio 
          title
          text
          btnTop
          btnLeft
        }
        carouselHotspot {
          order
          images {
            image 
            imageAlt
          }
          title
          text
          btnTop
          btnLeft
        }
      }
    }
  }
`
//  videoHotspot {
//     order
//     video 
//     title
//     text
//     btnTop
//     btnLeft
//   }